
import { Ticket, SLARule, SLAStatus, TicketStatus, SLAPriority } from '../types';
import { SLA_RULES, SLA_AT_RISK_THRESHOLD_PERCENTAGE } from '../constants';

interface CalculatedSLAInfo {
  respondByDate?: string;
  resolveByDate?: string;
  responseSlaStatus: SLAStatus;
  resolutionSlaStatus: SLAStatus;
}

export const calculateSlaForTicket = (ticket: Ticket): CalculatedSLAInfo => {
  const slaRule = SLA_RULES.find(rule => rule.priority === ticket.priority);

  if (!slaRule || ticket.status === TicketStatus.CUSTOMER_DECLINED) {
    return {
      responseSlaStatus: SLAStatus.NOT_APPLICABLE,
      resolutionSlaStatus: SLAStatus.NOT_APPLICABLE,
    };
  }

  const createdAt = new Date(ticket.createdAt);
  const now = new Date();

  // --- Response SLA ---
  const respondByDate = new Date(createdAt.getTime() + slaRule.respondWithinHours * 60 * 60 * 1000);
  let responseSlaStatus: SLAStatus;

  // For response SLA, we don't have an actual "first response time" in this conceptual model.
  // We'll determine status based on whether the 'respondByDate' has passed.
  if (now > respondByDate) {
    responseSlaStatus = SLAStatus.BREACHED; // Assuming no response logged by this time means breached
  } else {
    const timeToRespondMs = respondByDate.getTime() - createdAt.getTime();
    const timeElapsedMs = now.getTime() - createdAt.getTime();
    if (timeElapsedMs / timeToRespondMs >= SLA_AT_RISK_THRESHOLD_PERCENTAGE) {
      responseSlaStatus = SLAStatus.AT_RISK;
    } else {
      responseSlaStatus = SLAStatus.OK;
    }
  }


  // --- Resolution SLA ---
  const resolveByDate = new Date(createdAt.getTime() + slaRule.resolveWithinHours * 60 * 60 * 1000);
  let resolutionSlaStatus: SLAStatus;

  if (ticket.status === TicketStatus.COMPLETED) {
    const completedAt = new Date(ticket.updatedAt); // Assuming updatedAt is when it was completed
    if (completedAt <= resolveByDate) {
      resolutionSlaStatus = SLAStatus.COMPLETED_ON_TIME;
    } else {
      resolutionSlaStatus = SLAStatus.COMPLETED_LATE;
    }
  } else { // Ticket is not yet completed
    if (now > resolveByDate) {
      resolutionSlaStatus = SLAStatus.BREACHED;
    } else {
      const timeToResolveMs = resolveByDate.getTime() - createdAt.getTime();
      const timeElapsedMs = now.getTime() - createdAt.getTime();
      if (timeElapsedMs / timeToResolveMs >= SLA_AT_RISK_THRESHOLD_PERCENTAGE) {
        resolutionSlaStatus = SLAStatus.AT_RISK;
      } else {
        resolutionSlaStatus = SLAStatus.OK;
      }
    }
  }


  return {
    respondByDate: respondByDate.toISOString(),
    resolveByDate: resolveByDate.toISOString(),
    responseSlaStatus,
    resolutionSlaStatus,
  };
};
